import type React from "react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import type { SimpleProjectDto } from "@/api/types";
import { Form } from "@/components/Form/Form";
import { FormHiddenInput } from "@/components/Form/FormHiddenInput";
import { FormInput } from "@/components/Form/FormInput";
import { FormMultiSelect } from "@/components/Form/FormMultiSelect";
import { FormTextArea } from "@/components/Form/FormTextArea";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { createEmailValidRule, createRequiredStringRule } from "@/helpers/rules";
import { parseAsNumber, sortAlphabetically } from "@/helpers/util";
import { useLinkedProjects } from "@/hooks/Network/useLinkedProjects";
import { useTranslation } from "@/translations";

import type { AlertType, SaveRequest } from "../AlertSettings";

const MIN_PERCENTAGE = 1;
const MAX_PERCENTAGE = 100;
const MIN_THRESHOLD = 1;
const FORM_ID = "alert-details-modal-form";

export interface AlertDetailFormValues {
  id: string;
  enabled: boolean;
  emailEnabled: boolean;
  type: AlertType;
  name: string;
  email: string;
  projects: SimpleProjectDto[];
  threshold?: string;
  keywords?: string;
}

type AlertDetailsModalProps = ModalBaseProps & {
  alert: AlertDetailFormValues;
  onSave: ({ id, type, payload }: { id: string; type: AlertType; payload: SaveRequest }) => Promise<void>;
};

export function AlertDetailsModal({ alert, isOpened, onOpenChange, onSave }: AlertDetailsModalProps): React.ReactNode {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { t } = useTranslation();
  const { data: projects = [] } = useLinkedProjects();
  const form = useForm<AlertDetailFormValues>({ defaultValues: alert });

  useEffect(() => {
    form.reset(alert);
  }, [alert, form]);

  function handleClose() {
    form.reset();
  }

  async function handleSave() {
    setIsSubmitting(true);

    const formValues = form.getValues();
    const payload: SaveRequest = {
      enabled: formValues.enabled,
      enableEmail: formValues.emailEnabled,
      name: formValues.name.trim(),
      email: formValues.email.trim(),
      projectIds: formValues.projects.map((project) => project.id),
      threshold: parseAsNumber(formValues.threshold),
      keywords: formValues.keywords?.split(","),
    };

    await onSave({ id: formValues.id, type: formValues.type, payload });
    form.reset();

    setIsSubmitting(false);
    onOpenChange(false);
  }

  function addAllProjects() {
    form.setValue("projects", projects);
  }

  function clearProjectSelection() {
    form.setValue("projects", []);
  }

  function getAlertTitle() {
    switch (alert.type) {
      case "reactions":
        return t("page.alerts.modal.alert-type.title.reactions");
      case "comments":
        return t("page.alerts.modal.alert-type.title.comments");
      case "keywords_post":
        return t("page.alerts.modal.alert-type.title.post-keywords");
      case "keywords_comments":
        return t("page.alerts.modal.alert-type.title.comment-keywords");
    }
  }

  function getAlertDescription() {
    switch (alert.type) {
      case "reactions":
        return t("page.alerts.modal.alert-type.description.reactions");
      case "comments":
        return t("page.alerts.modal.alert-type.description.comments");
      case "keywords_post":
        return t("page.alerts.modal.alert-type.description.post-keywords");
      case "keywords_comments":
        return t("page.alerts.modal.alert-type.description.comment-keywords");
    }
  }

  return (
    <Modal.Root
      title={alert.id ? getAlertTitle() : t("page.alerts.modal.create.title")}
      description={alert.id ? getAlertDescription() : undefined}
      isOpened={isOpened}
      onOpenChange={onOpenChange}
    >
      <Modal.Body>
        <Form.Root formMethods={form} id={FORM_ID} onSubmit={handleSave}>
          <Form.Body>
            <FormHiddenInput<AlertDetailFormValues> name="id" />
            <FormHiddenInput<AlertDetailFormValues> name="enabled" />
            <FormHiddenInput<AlertDetailFormValues> name="emailEnabled" />
            <FormHiddenInput<AlertDetailFormValues> name="type" />
            <FormField label={t("page.alerts.modal.form.name.title")} required>
              <FormInput<AlertDetailFormValues, "name">
                name="name"
                placeholder={t("page.alerts.modal.form.name.placeholder")}
                rules={{
                  validate: {
                    required: createRequiredStringRule(t, "page.alerts.modal.form.name.title"),
                  },
                }}
              />
            </FormField>
            <FormField
              label={t("page.alerts.modal.form.email.title")}
              tooltip={t("page.alerts.modal.form.email.tooltip")}
              required
            >
              <FormInput<AlertDetailFormValues, "email">
                name="email"
                placeholder={t("page.alerts.modal.form.email.placeholder")}
                rules={{
                  validate: {
                    required: createRequiredStringRule(t, "page.alerts.modal.form.email.title"),
                    isValid: createEmailValidRule(t),
                  },
                }}
              />
            </FormField>
            <Stack gap="md">
              <FormField label={t("page.alerts.modal.form.projects.title")} required>
                <FormMultiSelect<AlertDetailFormValues, "projects">
                  name="projects"
                  placeholder={t("page.alerts.modal.form.projects.placeholder")}
                  items={projects || []}
                  searchableFieldSelector={(project) => [project.name, project.owner, project.maintenance]}
                  renderOption={(project) => project.name}
                  keySelector={(project) => project.id}
                  sortSelectedItems={sortAlphabetically}
                  usePortal
                  rules={{
                    required: t("components.form.error.required", {
                      inputName: t("page.alerts.modal.form.projects.title"),
                    }),
                  }}
                />
              </FormField>
              <Inline gap="sm" justify="end">
                <Button variant="soft" onClick={addAllProjects}>
                  {t("page.alerts.modal.form.projects.add-all")}
                </Button>
                <Button variant="subtle" tone="error" onClick={clearProjectSelection}>
                  {t("page.alerts.modal.form.projects.clear-all")}
                </Button>
              </Inline>
            </Stack>
            {alert.type === "reactions" && (
              <FormField
                label={t("page.alerts.modal.form.percentage-threshold.title")}
                tooltip={t("page.alerts.modal.form.percentage-threshold.tooltip")}
                required
              >
                <FormInput<AlertDetailFormValues>
                  name="threshold"
                  inputMode="numeric"
                  type="number"
                  placeholder={t("page.alerts.modal.form.percentage-threshold.placeholder")}
                  rules={{
                    required: t("components.form.error.required", {
                      inputName: t("page.alerts.modal.form.percentage-threshold.title"),
                    }),
                    min: {
                      message: t("page.alerts.modal.form.percentage-threshold.rules.min", { count: MIN_PERCENTAGE }),
                      value: MIN_PERCENTAGE,
                    },
                    max: {
                      message: t("page.alerts.modal.form.percentage-threshold.rules.max", { count: MAX_PERCENTAGE }),
                      value: MAX_PERCENTAGE,
                    },
                  }}
                />
              </FormField>
            )}
            {alert.type === "comments" && (
              <FormField
                label={t("page.alerts.modal.form.exact-threshold.title")}
                tooltip={t("page.alerts.modal.form.exact-threshold.tooltip")}
                required
              >
                <FormInput<AlertDetailFormValues>
                  name="threshold"
                  inputMode="numeric"
                  type="number"
                  placeholder={t("page.alerts.modal.form.exact-threshold.placeholder")}
                  rules={{
                    required: t("components.form.error.required", {
                      inputName: t("page.alerts.modal.form.exact-threshold.title"),
                    }),
                    min: {
                      message: t("page.alerts.modal.form.exact-threshold.rules.min", { count: MIN_THRESHOLD }),
                      value: MIN_THRESHOLD,
                    },
                  }}
                />
              </FormField>
            )}
            {(alert.type === "keywords_post" || alert.type === "keywords_comments") && (
              <FormField
                label={t("page.alerts.modal.form.keywords.title")}
                description={t("page.alerts.modal.form.keywords.subtitle")}
                tooltip={t("page.alerts.modal.form.keywords.tooltip")}
                required
              >
                <FormTextArea<AlertDetailFormValues>
                  name="keywords"
                  placeholder={t("page.alerts.modal.form.keywords.placeholder")}
                  rules={{
                    required: t("components.form.error.required", {
                      inputName: t("page.alerts.modal.form.keywords.title"),
                    }),
                  }}
                />
              </FormField>
            )}
          </Form.Body>
        </Form.Root>
      </Modal.Body>
      <Modal.Actions>
        <Modal.Close>
          <Button variant="outline" tone="primary" onClick={handleClose}>
            {t("common.action.cancel")}
          </Button>
        </Modal.Close>
        <Button variant="filled" form={FORM_ID} type="submit" isLoading={isSubmitting}>
          {t("common.action.save")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
